import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { BoardDropdown } from "@/components/QuickSearch/components/BoardDropdown/BoardDropdown";
import type { BoardDropdownOption } from "@/components/QuickSearch/components/BoardDropdown/models/boardDropdownOption";
import type { DropdownOptionBase } from "@/components/QuickSearch/components/DropdownList/models/dropdownOptions";
import { formatToBoardDropdownOption } from "@/components/QuickSearch/components/DropdownList/utils/formatToDropdownOption";
import QuickSearchContainer from "@/components/QuickSearch/components/styles/QuickSearchStyle";
import { getSearchBoardsData } from "@/components/QuickSearch/state/mondaySearchReducer";

type QuickSearchProps = {
  onBoardChange: (boardId: string) => void;
  clearValue: boolean;
};

/**
 * This is a barebones quick-search component that can render different instances of Monday dropdown.
 * @param selRef
 * @param onBoardChange
 * @constructor
 */
export function QuickSearch({ onBoardChange, clearValue }: QuickSearchProps) {
  const { data: boards, isLoading: isLoadingBoards } = useSelector(getSearchBoardsData);

  const boardsToOptions = useMemo<BoardDropdownOption[]>(() => {
    return boards.map(formatToBoardDropdownOption);
  }, [boards]);

  const [boardOption, setBoardOption] = useState<BoardDropdownOption | null>(null);

  useEffect(() => {
    setBoardOption(null);
  }, [clearValue]);

  const changeCurrentBoard = useCallback(
    (data: DropdownOptionBase) => {
      setBoardOption(data as BoardDropdownOption);
      onBoardChange(`${data.value}`);
    },
    [setBoardOption, onBoardChange]
  );

  return (
    <QuickSearchContainer>
      <BoardDropdown
        value={boardOption}
        options={boardsToOptions}
        onOptionSelect={changeCurrentBoard}
        isLoadingBoards={isLoadingBoards}
        isOptionSelected={false}
      />
    </QuickSearchContainer>
  );
}
